import {
  Circle,
  DragBox,
  Draw,
  Feature,
  Fill,
  Geometry,
  LineString,
  Map,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
  Stroke,
  Style,
  VectorLayer,
  VectorSource
} from '../../models'

import {v4 as uuidv4} from 'uuid'
import {MapService} from '..'
import {DrawSource} from '../../enums/DrawSource'
import {DrawType} from '../../enums/DrawType'
import {GeometryType} from '../../enums/GeometryType'
import {IConfig} from '../../interfaces/reducers'
import {IMapDrawingService} from '../../interfaces/services'
import {unByKey} from 'ol/Observable'
import {getArea, getLength} from 'ol/sphere'
import get from 'lodash/get'

export class MapDrawingService implements IMapDrawingService {
  public map: Map
  protected config: IConfig
  private draw: Draw
  private dragBox: DragBox
  protected mapService: MapService
  protected drawLayers: {[drawSource: string]: {[drawType: string]: VectorLayer}} = {}
  protected drawStyles: {[drawSource: string]: {[drawType: string]: Style}} = {}

  constructor(config: IConfig, map: Map, mapService: MapService) {
    this.map = map
    this.config = config
    this.mapService = mapService
    this.configureDrawStyles()
    this.configureDrawLayers()
  }

  protected configureDrawStyles() {
    const drawSourceKeys = Object.keys(DrawSource)
    const drawTypeKeys = [DrawType.Point, DrawType.LineString, DrawType.Polygon]
    drawSourceKeys.forEach((k, i) => {
      if (!this.drawStyles[(DrawSource as any)[k]]) {
        this.drawStyles[(DrawSource as any)[k]] = {}
      }
      drawTypeKeys.forEach((t, j) => {
        this.drawStyles[(DrawSource as any)[k]][DrawType[t]] = this.getDrawStyle(DrawType[t], (DrawSource as any)[k])
      })
    })
  }

  protected configureDrawLayers() {
    const drawSourceKeys = Object.keys(DrawSource)
    const drawTypeKeys = [DrawType.Point, DrawType.LineString, DrawType.Polygon]
    drawSourceKeys.forEach((k, i) => {
      if (!this.drawLayers[(DrawSource as any)[k]]) {
        this.drawLayers[(DrawSource as any)[k]] = {}
      }
      drawTypeKeys.forEach((t, j) => {
        const layer = new VectorLayer({
          source: new VectorSource(),
          style: this.drawStyles[(DrawSource as any)[k]][DrawType[t]]
        })
        layer.setZIndex(900 + (i + 1) * 2 + j)
        layer.moreInfo = {
          urls: [],
          guid: uuidv4(),
          type: 'draw',
          refreshOnExtentChange: false,
          isActive: true
        }
        this.map.addLayer(layer)
        this.drawLayers[(DrawSource as any)[k]][DrawType[t]] = layer
      })
    })
  }

  protected getGrometryClass(drawType: DrawType) {
    switch (drawType) {
      case DrawType.Point:
        return Point
      case DrawType.LineString:
        return LineString
      case DrawType.Circle:
      case DrawType.Rectangle:
      case DrawType.Polygon:
        return Polygon
      case DrawType.MultiLineString:
        return MultiLineString
      case DrawType.MultiPoint:
        return MultiPoint
      case DrawType.MultiPolygon:
        return MultiPolygon
      default:
        return Point
    }
  }
  protected onDrawEnd(feat: Feature, drawType: DrawType, callBackOnComplete?: (feature: Feature) => void) {
    this.deactivateDraw(false)
    const feature = new Feature()
    const coords = feat.getGeometry().getCoordinates()
    if (feat.getGeometry().getType() == GeometryType.Polygon) {
      ;(coords as number[][][]).forEach(ring => {
        if (ring[0][0] == ring[ring.length - 1][0] && ring[0][1] == ring[ring.length - 1][1]) {
          ring.splice(ring.length - 1, 1)
        }
      })
    }
    if (feat.getGeometry().getType() == GeometryType.MultiPolygon) {
      ;(coords as number[][][][]).forEach(poly => {
        poly.forEach(ring => {
          if (ring[0][0] == ring[ring.length - 1][0] && ring[0][1] == ring[ring.length - 1][1]) {
            ring.splice(ring.length - 1, 1)
          }
        })
      })
    }
    const geom = new (this.getGrometryClass(drawType) as any)(coords)
    feature.setGeometry(geom)
    if (callBackOnComplete) {
      callBackOnComplete(feature)
    }
  }
  private latestOnDeactivated: () => void

  latestOnMoveLabel: any
  handleDrawDelta = (
    startPoint: number[],
    onStart: () => void,
    callback: (dX: number, dY: number) => void,
    onEnd: () => void
  ) => {
    this.latestOnMoveLabel = (event: any) => {
      if (event && event.coordinate) {
        callback(event.coordinate[0] - startPoint[0], event.coordinate[1] - startPoint[1])
      }
    }
    this.map.on('pointermove', this.latestOnMoveLabel)
    this.draw.on('drawstart', (event: any) => {
      onStart()
    })
    this.draw.on('drawend', (event: any) => {
      this.map.un('pointermove', this.latestOnMoveLabel)
      onEnd()
    })
  }

  protected getBestLabeledFeatureToMove = (
    layersToAlter: VectorSource[],
    startPoint: number[]
  ): Feature | undefined => {
    let features: Feature[] = []
    layersToAlter.forEach(l => {
      // let feats = l.getFeaturesAtCoordinate(startPoint);
      const feats = l.getFeaturesInExtent([
        startPoint[0] - 500,
        startPoint[1] - 500,
        startPoint[0] + 500,
        startPoint[1] + 500
      ])
      features = features.concat(feats as any as Feature[])
    })
    if (features.length > 1) {
      let distance = 100000000
      let nearest: Feature | undefined = undefined
      features.forEach(f => {
        const geom = f.getGeometry()
        if (geom) {
          let center = geom.getCenter().getCoordinates()
          const existing = f.get('___styleDelta')
          if (existing) {
            center = [existing[0] + center[0], existing[1] + center[1]]
          }
          const dist = Math.sqrt(Math.pow(center[0] - startPoint[0], 2) + Math.pow(center[1] - startPoint[1], 2))
          if (dist < distance) {
            distance = dist
            nearest = f
          }
        }
      })
      return nearest
    }
    if (features.length == 1) {
      return features[0]
    }
    return undefined
  }
  public activateLabelMoving(layerIds: string[], onDeactivate: () => void) {
    this.deactivateDraw(false)
    this.latestOnDeactivated = onDeactivate
    setTimeout(() => {
      this.mapService.mapDiv.style.cursor = 'pointer'
    }, 100)
    const layersToAlter: VectorSource[] = []
    this.mapService.getLayers().forEach(l => {
      const source = l.getSource()
      if (source instanceof VectorSource) {
        l.moreInfo.urls.forEach(u => {
          if (layerIds.indexOf(u) != -1) {
            layersToAlter.push(source as VectorSource)
          }
        })
      }
    })
    if (layersToAlter.length > 0) {
      this.draw = new Draw({
        style: (f: Feature) => {
          return new Style({
            stroke: new Stroke({
              color: 'rgba(150, 153, 255, 0)',
              width: 6
            })
          })
        },
        maxPoints: 2,
        type: DrawType.LineString,
        stopClick: true
      } as any)
      this.draw.on('drawstart', (event: any) => {
        const startPoint = event.feature.getGeometry().getCoordinates()[0]
        const bestF = this.getBestLabeledFeatureToMove(layersToAlter, startPoint)!
        if (bestF) {
          this.handleDrawDelta(
            startPoint,
            () => {
              if (bestF.get('___styleDelta')) {
                bestF.set('___styleDeltaFinished', bestF.get('___styleDelta'))
              }
              this.mapService.mapDiv.style.cursor = 'move'
            },
            (dX, dY) => {
              const existing = bestF.get('___styleDeltaFinished')
              if (existing) {
                bestF.set('___styleDelta', [existing[0] + dX, existing[1] + dY])
              } else {
                bestF.set('___styleDelta', [dX, dY])
              }
            },
            () => {
              bestF.set('___styleDeltaFinished', undefined)
              this.mapService.mapDiv.style.cursor = 'pointer'
            }
          )
        }
      })

      this.map.addInteraction(this.draw)
      this.mapService.onStartInteraction('drawing')
    }
  }

  public formatLength(line: LineString) {
    const length = getLength(line)
    const output = Math.round(length * 100) / 100 + ' ' + 'm'
    return output
  }

  public formatArea(polygon: Polygon) {
    const area = getArea(polygon)
    const output = Math.round(area * 100) / 100 + ' ' + 'm<sup>2</sup>'
    return output
  }

  public activateDraw(
    drawType: DrawType,
    drawSource: DrawSource,
    onDeactivated: () => void,
    callBackOnComplete?: (feature: Feature) => void,
    maxPoints?: number
  ) {
    this.deactivateDraw(false)
    this.latestOnDeactivated = onDeactivated

    if (drawType == 'Rectangle') {
      this.dragBox = new DragBox()
      const dragBoxStyle = this.drawStyles[drawSource][DrawType.Polygon]
      const box = (this.dragBox as any).box_.element_
      const stroke = dragBoxStyle.getStroke()
      if (stroke) {
        box.style.border = stroke.getWidth() + 'px solid ' + stroke.getColor()
      }
      const fill = dragBoxStyle.getFill()
      if (fill) {
        box.style.backgroundColor = fill.getColor()
      }
      this.dragBox.on('boxend', () => {
        this.onDrawEnd(new Feature({geometry: this.dragBox.getGeometry()}), drawType, callBackOnComplete)
      })
      this.map.addInteraction(this.dragBox)
    } else {
      this.draw = new Draw({
        style: (f: Feature) => {
          const geom = f.getGeometry()
          if ((geom.getType() as GeometryType) == GeometryType.Polygon) {
            return this.drawStyles[drawSource][DrawType.Polygon]
          } else if ((geom.getType() as GeometryType) == GeometryType.LineString) {
            return this.drawStyles[drawSource][DrawType.LineString]
          }
          return this.drawStyles[drawSource][DrawType.Point]
        },
        maxPoints: maxPoints,
        type: drawType,
        stopClick: true
      } as any)
      // show line length on draw

      this.draw.on('drawstart', (event: any) => {
        const feature = event.feature
        const geom = feature.getGeometry()
        const measure = document.getElementById('measure')

        if ((geom.getType() as GeometryType) == GeometryType.LineString) {
          const listener = geom.on('change', (e: any) => {
            const geom = e.target
            const output = this.formatLength(geom)

            if (measure) measure.innerHTML = output
            this.mapService.mapDiv.title = output
          })
          this.latestOnDeactivated = () => {
            if (measure) measure.innerHTML = ''
            this.mapService.mapDiv.title = ''
            unByKey(listener)
          }
        } else if ((geom.getType() as GeometryType) == GeometryType.Polygon) {
          const listener = geom.on('change', (e: any) => {
            const geom = e.target
            const output = this.formatArea(geom)
            if (measure) measure.innerHTML = output
            this.mapService.mapDiv.title = output
          })
          this.latestOnDeactivated = () => {
            if (measure) measure.innerHTML = ''
            this.mapService.mapDiv.title = ''
            unByKey(listener)
          }
        }
      })

      this.draw.on('drawend', (event: any) => {
        this.onDrawEnd(event.feature, drawType, callBackOnComplete)
      })
      this.map.addInteraction(this.draw)
    }
    this.mapService.onStartInteraction('drawing')
  }

  public deactivateDraw(clear: boolean) {
    if (this.latestOnMoveLabel) {
      this.map.un('pointermove', this.latestOnMoveLabel)
    }
    if (this.draw) {
      this.draw.setActive(false)
      this.map.removeInteraction(this.draw)
    }
    if (this.dragBox) {
      this.map.removeInteraction(this.dragBox)
    }
    if (clear) {
      this.latestOnDeactivated = () => {}
    } else if (this.latestOnDeactivated) {
      this.latestOnDeactivated()
    }
    this.mapService.onEndInteraction('drawing')
  }

  protected normalizeDrawType(drawType: DrawType): DrawType {
    if (drawType == DrawType.Point || drawType == DrawType.MultiPoint) {
      return DrawType.Point
    } else if (
      drawType == DrawType.Polygon ||
      drawType == DrawType.Rectangle ||
      drawType == DrawType.Circle ||
      drawType == DrawType.MultiPolygon
    ) {
      return DrawType.Polygon
    } else if (drawType == DrawType.LineString || drawType == DrawType.MultiLineString) {
      return DrawType.LineString
    } else {
      throw new Error('no such type')
    }
  }

  public getDrawStyle(drawType: DrawType, drawSource: DrawSource) {
    const normalizedDrawType = this.normalizeDrawType(drawType)
    if (normalizedDrawType == DrawType.Point) {
      return this.getPointStyle(drawSource)
    } else if (normalizedDrawType == DrawType.Polygon) {
      return this.getPolygonStyle(drawSource)
    } else if (normalizedDrawType == DrawType.LineString) {
      return this.getLineStringStyle(drawSource)
    } else {
      throw new Error('no such type')
    }
  }

  public getPointStyle(drawSource: DrawSource) {
    if (drawSource == DrawSource.Edit) {
      return new Style({
        image: new Circle({
          radius: 5,
          fill: new Fill({color: 'rgba(255, 255, 255, 0.5)'}),
          stroke: new Stroke({
            color: 'rgb(0, 153, 255)',
            width: 3
          })
        })
      })
    }
    if (drawSource == DrawSource.Red) {
      return new Style({
        image: new Circle({
          radius: 5,
          fill: new Fill({color: 'rgba(255, 255, 255, 0.5)'}),
          stroke: new Stroke({
            color: 'rgb(0, 153, 255)',
            width: 3
          })
        })
      })
    }
    return new Style({
      image: new Circle({
        radius: 7,
        fill: new Fill({color: 'rgba(255, 255, 255, 0.5)'}),
        stroke: new Stroke({
          color: 'rgb(0, 153, 255)',
          width: 2
        })
      })
    })
  }

  public getLineStringStyle(drawSource: DrawSource) {
    return new Style({
      stroke: new Stroke({
        color: 'rgb(0, 153, 255)',
        width: drawSource == DrawSource.Select ? 4 : 2
      })
    })
  }

  public getPolygonStyle(drawSource: DrawSource) {
    if (drawSource == DrawSource.Edit) {
      return new Style({
        fill: new Fill({
          color: 'rgba(255, 255, 255, 0.5)'
        }),
        stroke: new Stroke({
          color: '#b34242',
          width: 2
        })
      })
    }
    if (drawSource == DrawSource.ExportSelect) {
      return new Style({
        fill: new Fill({
          color: 'rgba(0, 0, 0, 0.05)'
        }),
        stroke: new Stroke({
          color: 'rgb(0, 0, 0, 0)',
          width: 2
        })
      })
    }
    return new Style({
      fill: new Fill({
        color: 'rgba(255, 255, 255, 0.5)'
      }),
      stroke: new Stroke({
        color: 'rgb(0, 153, 255)',
        width: 2
      })
    })
  }

  public mark(features: Array<Feature>, drawSource: DrawSource) {
    features = features.filter(feat => !!feat.getGeometry())
    features.forEach(f => {
      const geom = f.getGeometry()
      const type = geom.getType() as GeometryType
      let source: VectorSource
      if (type == GeometryType.Point) {
        source = this.drawLayers[drawSource][DrawType.Point].getSource()
      } else if (type == GeometryType.LineString || type == GeometryType.MultiLineString) {
        source = this.drawLayers[drawSource][DrawType.LineString].getSource()
      } else if (type == GeometryType.Polygon || type == GeometryType.MultiPolygon) {
        source = this.drawLayers[drawSource][DrawType.Polygon].getSource()
      } else {
        throw new Error(`Type ${geom.getType()} not implemented yet`)
      }
      const existing = source.getFeatureById(f.getId())
      if (existing) {
        source.removeFeature(existing as any)
      }
      source.addFeature(f as any)
    })
  }

  public unMark(drawSource: DrawSource, features?: Array<Feature>) {
    if (features) {
      const featureIds = features.filter(g => !!g).map(g => g.guid)
      if (featureIds.length > 0) {
        const pointSource = this.drawLayers[drawSource][DrawType.Point].getSource()
        pointSource.forEachFeature((f: any) => {
          if (f.guid && featureIds.indexOf(f.guid) != -1) {
            pointSource.removeFeature(f as any)
          }
        })

        const polylineSource = this.drawLayers[drawSource][DrawType.LineString].getSource()
        polylineSource.forEachFeature((f: any) => {
          if (f.guid && featureIds.indexOf(f.guid) != -1) {
            polylineSource.removeFeature(f as any)
          }
        })

        const polygonSource = this.drawLayers[drawSource][DrawType.Polygon].getSource()
        polygonSource.forEachFeature((f: any) => {
          if (f.guid && featureIds.indexOf(f.guid) != -1) {
            polygonSource.removeFeature(f as any)
          }
        })
      }
    } else {
      const pointSource = this.drawLayers[drawSource][DrawType.Point].getSource()
      pointSource.forEachFeature(f => pointSource.removeFeature(f as any))

      const polylineSource = this.drawLayers[drawSource][DrawType.LineString].getSource()
      polylineSource.forEachFeature(f => polylineSource.removeFeature(f as any))

      const polygonSource = this.drawLayers[drawSource][DrawType.Polygon].getSource()
      polygonSource.forEachFeature(f => polygonSource.removeFeature(f as any))
    }
  }

  public flash(geometries: Geometry[]) {
    const span = 400
    const features: Feature[] = []
    geometries.forEach(g => {
      const f = new Feature()
      f.setGeometry(g)
      features.push(f)
    })
    this.mark(features, DrawSource.Select)
    setTimeout(() => {
      this.unMark(DrawSource.Select, features)
      setTimeout(() => {
        this.mark(features, DrawSource.Select)
        setTimeout(() => {
          this.unMark(DrawSource.Select, features)
        }, span)
      }, span)
    }, span)
  }
}
